import React from 'react';
import {
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { SummaryQuestionData, NumAnswerData } from '~/common/types/api/users';
import { QUESTION_TYPE_LABEL } from '~/common/constants';

const ContentBox = styled.div`
  width: 100%;
  padding: 25px;
  margin: 20px 0;
  border: 1px solid ${PALETTE.border.frame};
  background: ${PALETTE.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const QuestionTitle = styled(Typography)`
  font-size: 20px;
  color: ${PALETTE.text.main};
  white-space: pre-line;
`;

const StyledTable = styled(Table)`
  border: 1px solid ${PALETTE.border.table};
`;

const StyledTableHeader = styled(TableCell)`
  background: ${PALETTE.blue.main};
  border: 1px solid ${PALETTE.border.table};
  text-align: center;
  color: ${PALETTE.text.main};
  font-size: ${FONT_SIZES.title4};
  padding: 7px 27px 7px 27px;
  height: 36px;
`;

const StyledTableCell = styled(TableCell)<StyledTableCellProps>`
  background: ${(props) =>
    props.$index !== undefined && props.$index ? PALETTE.white : PALETTE.blue.light};
  color: ${PALETTE.text.main};
  font-size: 16px;
  padding: 7px 27px 7px 27px;
  height: 36px;
`;

type StyledTableCellProps = {
  $index?: boolean;
};

type Props = {
  question: SummaryQuestionData;
  questionIdx: number;
};

const NumAnswer: React.FC<Props> = ({ question, questionIdx }: Props) => {
  const { t } = useTranslation();
  let data: NumAnswerData = {
    numberOfSample: 0,
    average: '-',
    median: '-',
    maximum: '-',
    minimum: '-',
    standardDeviation: '-',
    variance: '-',
  };

  const removeTags = (str: string) => {
    const regex = /<[^>]+>/g;
    return str.replace(regex, '');
  };

  if (question.numAnswer) {
    data = question.numAnswer;
  }

  return (
    <ContentBox>
      <QuestionTitle data-testid="question-label">
        {`${questionIdx + 1}. ${removeTags(question.questionLabel)} [${
          (QUESTION_TYPE_LABEL as Record<number, string>)[question.questionType]
        }]`}
      </QuestionTitle>

      <TableContainer
        sx={{ marginTop: '30px', marginBottom: '30px' }}
        component="div"
        data-testid="question-table"
      >
        <StyledTable>
          <TableHead data-testid="table-header">
            <TableRow>
              <StyledTableHeader sx={{ width: '490px' }}>
                {t('webAggregation.tableHeader.answer')}
              </StyledTableHeader>
              <StyledTableHeader sx={{ width: '205px' }}>
                {t('webAggregation.tableHeader.number')}
              </StyledTableHeader>
            </TableRow>
          </TableHead>

          <TableBody data-testid="question-table-body">
            <TableRow>
              <StyledTableCell>N数</StyledTableCell>
              <StyledTableCell align="right">{data.numberOfSample.toFixed(0)}</StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell $index>平均</StyledTableCell>
              <StyledTableCell $index align="right">
                {data.average}
              </StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell>中央</StyledTableCell>
              <StyledTableCell align="right">{data.median}</StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell $index>標準偏差</StyledTableCell>
              <StyledTableCell $index align="right">
                {data.standardDeviation}
              </StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell $index>分散</StyledTableCell>
              <StyledTableCell align="right">{data.variance}</StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell $index>最大</StyledTableCell>
              <StyledTableCell $index align="right">
                {data.maximum}
              </StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell>最小</StyledTableCell>
              <StyledTableCell align="right">{data.minimum}</StyledTableCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
    </ContentBox>
  );
};
export default NumAnswer;
